import React, { createContext, useCallback, useEffect, useReducer } from 'react';

// third-party
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project import
import Loader from 'components/Loader';
import { setRequestInterceptors } from 'utils/axios';
import { KeyedObject } from 'types/root';
import { AuthProps, AuthContextType } from 'types/auth';
import { User } from 'types/auth';
import authService from 'authorization/AuthorizeService';

// constant
const initialState: AuthProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    authService: authService,
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded: KeyedObject = jwtDecode(serviceToken);
    /**
     * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};

// ==============================|| Auth CONTEXT & PROVIDER ||============================== //

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = await state.authService?.getAccessToken();
                if (serviceToken && verifyToken(serviceToken)) {
                    setRequestInterceptors(serviceToken);
                    const isTokenExpired = await authService.isTokenExpired();
                    if (isTokenExpired) {
                        await authService.tryRefreshToken();
                    }
                    const user = await state.authService?.getUser();
                    if (user) {
                        void authService.synchronizeUser(user);
                        dispatch({
                            type: LOGIN,
                            payload: {
                                isLoggedIn: true,
                                user,
                            },
                        });
                    }
                } else {
                    dispatch({
                        type: LOGOUT,
                    });
                }
            } catch (err) {
                dispatch({
                    type: LOGOUT,
                });
            }
        };

        init();
    }, [state.authService]);

    const login = useCallback((user: User) => {
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user,
            },
        });
    }, []);

    const logout = useCallback(() => {
        setRequestInterceptors(null);
        dispatch({ type: LOGOUT });
    }, []);

    useEffect(() => {
        const initializeAppWithToken = async () => {
            const isTokenExpired = await authService.isTokenExpired();
            authService.subscribe(() => {
                authService.getUser().then((user) => {
                    if (user)
                        dispatch({
                            type: LOGIN,
                            payload: {
                                isLoggedIn: true,
                                user,
                            },
                        });
                    else {
                        setRequestInterceptors(null);
                        dispatch({
                            type: LOGOUT,
                        });
                    }
                });
            });

            if (isTokenExpired) {
                await authService.tryRefreshToken();
            }
        };

        initializeAppWithToken();
    }, [state.isLoggedIn]);

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>;
};

export default AuthContext;
